import { cpf } from 'cpf-cnpj-validator'
import { PATIENT_LIST } from 'navigation/CONSTANTS'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from 'reduxStore/store'
import { handler, useToast } from 'services/context/toast'
import { addPatientInEstablishment } from 'services/establishment'
import { postPatientRegistration, searchPatientsByCPF } from 'services/patient'
import Storage from 'storage/auth'
import { IPostPatientEstablishmentRelation } from 'types'
import { IPatientFormRequest } from 'types/patient/types'
import { trimStringInJSON } from 'utils/formatString'
import { validateEmail } from 'utils/masks'

type PatientFormKeys = keyof IPatientFormRequest

type AllFormKeys = PatientFormKeys | 'password'

type ErrorMap = Map<AllFormKeys, string>

const useAddPatient = () => {
  const user = Storage.user.get()

  const navigate = useNavigate()

  const [state, setState] = useState<IStateStdTypes>('idle')
  const [isModalOpen, setModal] = useState(false)
  const [request, setRequest] = useState<IPatientFormRequest>({
    first_name: '',
    last_name: '',
    cpf: '',
    email: '',
    mobile_phone: ''
  })

  const [password, setPassword] = useState<string>('')
  const [errorsMap, setErrorsMap] = useState<ErrorMap>(new Map())

  const establishmentId = useAppSelector(
    rState => rState.establishment.data?.establishment_id
  )

  const disableFields =
    state == 'idle' || state == 'loading' || !!request.patient_id
  const toast = useToast()

  const enableConfirmarButton = () => {
    const listOfRequiredInput: Array<PatientFormKeys> = [
      'first_name',
      'last_name',
      'cpf',
      'email',
      'mobile_phone'
    ]

    return (
      (request.patient_id || password != '') &&
      !listOfRequiredInput
        .map(key => request[key] == '')
        .reduce((a, b) => a || b, false)
    )
  }

  const updatePatient = (key: PatientFormKeys, value: string) => {
    resetInputError(key)
    setRequest({ ...request, [key]: value })
  }

  const updatePassword = (value: string) => {
    resetInputError('password')
    setPassword(value)
  }

  const clearErrorsMap = () => {
    errorsMap.clear()
    setErrorsMap(new Map())
  }

  const resetInputError = (key: AllFormKeys) => {
    if (errorsMap.has(key)) {
      errorsMap.delete(key)
      setErrorsMap(new Map(errorsMap))
    }
  }

  const searchPatient = async () => {
    setState('loading')
    const response = await searchPatientsByCPF(
      request.cpf.replaceAll(/[^\d]/g, '')
    )
    if (response.success) {
      const result = response.data

      if (result) {
        const data = {
          ...request,
          first_name: result?.first_name,
          last_name: result?.last_name,
          email: result?.email,
          mobile_phone: result?.mobile_phone,
          patient_id: result?.patient_id
        }

        setRequest(data)

        clearErrorsMap()

        setState('success')
      }
    } else {
      if (
        response.errors?.some(error =>
          ['not found', 'patient not found'].includes(
            error?.detail?.toLowerCase()
          )
        )
      ) {
        setState('success')
        if (request.patient_id) {
          clearPatient(request.cpf)
        } else {
          setRequest({ ...request, cpf: request.cpf })
        }
      } else {
        setState('error')
      }
    }
  }

  const linkPatient = async () => {
    if (!request.patient_id || !user) return

    const data: IPostPatientEstablishmentRelation = {
      establishment_id: establishmentId ?? '',
      patients: [request.patient_id]
    }

    const response = await addPatientInEstablishment(data)

    let state: IStateStdTypes
    let message: string | undefined

    if (response.success && response.data) {
      message = 'Paciente adicionado com sucesso!'
      clearPatient()
      state = 'success'
    } else {
      if (
        response.errors?.some(
          error =>
            error?.detail?.includes('healthcare_patient_relation_pkey') &&
            (error?.detail?.includes('duplicate key value') ||
              error?.detail?.includes('duplicar valor da chave'))
        )
      ) {
        message = 'Paciente já foi adicionado na organização!'
        clearPatient()
        state = 'idle'
      } else {
        message = 'Não foi possível adicionar o paciente na organização!'
        state = 'error'
      }
    }

    if (message) {
      toast.handler({
        content: message,
        severity: state === 'idle' ? 'info' : state
      })
    }
    setState(state)
    return state === 'success'
  }

  const validateForm = (): ErrorMap => {
    const newErrorsMap: ErrorMap = new Map()

    const {
      cpf: cpfField,
      first_name,
      last_name,
      mobile_phone,
      email
    } = request

    if (!cpf.isValid(cpfField)) {
      newErrorsMap.set('cpf', 'Digite um CPF válido')
    }
    if (first_name.length < 3) {
      newErrorsMap.set('first_name', 'Digite um nome válido')
    }
    if (last_name.length == 0) {
      newErrorsMap.set('last_name', 'Digite o sobrenome')
    }
    if (mobile_phone.length !== 15) {
      newErrorsMap.set('mobile_phone', 'Número de telefone inválido')
    }
    if (!validateEmail(email)) {
      newErrorsMap.set('email', 'Email inválido')
    }
    if (password.length < 6) {
      newErrorsMap.set('password', 'Senha precisa ter 6 ou mais caracteres')
    }

    errorsMap.clear()

    setErrorsMap(newErrorsMap)

    return newErrorsMap
  }

  const redirectTo = () =>
    navigate(`/${PATIENT_LIST}`, {
      replace: true
    })

  const clearPatient = (cpf?: string) => {
    setRequest({
      patient_id: undefined,
      first_name: '',
      last_name: '',
      cpf: cpf ?? '',
      email: '',
      mobile_phone: ''
    })
    setPassword('')

    clearErrorsMap()
  }

  const addNewPatient = async () => {
    if (state == 'loading') return

    const errors = validateForm()
    if (errors.size == 0) {
      setState('loading')

      const formatRequest = trimStringInJSON<IPatientFormRequest>(request)

      const response = await postPatientRegistration(
        {
          ...formatRequest,
          cpf: formatRequest.cpf.replaceAll(/[^\d]/g, ''),
          mobile_phone: formatRequest.mobile_phone.replaceAll(/[^\d]/g, ''),
          password: password
        },
        establishmentId ?? ''
      )

      if (response.success) {
        handler({
          content: 'paciente cadastrado com sucesso',
          severity: 'success'
        })
        setState('success')

        clearPatient()
        redirectTo()
      } else {
        if (
          response.errors?.some(
            error =>
              error?.detail?.includes('idx_patient_registration_cpf') &&
              (error?.detail?.includes('duplicate key value') ||
                error?.detail?.includes('duplicar valor da chave'))
          )
        ) {
          handler({
            content: `${formatRequest.cpf} já cadastrado`,
            severity: 'error'
          })
        } else {
          handler({
            content: 'Não foi possível cadastrar o Paciente',
            severity: 'error'
          })
        }
        setState('error')
      }
    }
  }

  const handleConfirm = async () => {
    const success = await linkPatient()
    setModal(false)
    if (success) {
      redirectTo()
    }
  }

  useEffect(() => {
    if (request.cpf.replaceAll(/[^\d]/g, '').length === 11) {
      searchPatient()
    }
  }, [request.cpf])

  return {
    disableFields,
    errors: errorsMap,
    isModalOpen,
    password,
    request,
    state,
    updatePatient,
    handleConfirm,
    setPassword: updatePassword,
    setModal,
    addNewPatient,
    enableButton: enableConfirmarButton()
  }
}

export default useAddPatient
